import React from "react";
import { toast } from "react-toastify";
import { useParams } from "react-router-dom";
import { Badge, Box, Button, Divider, Typography } from "@mui/material";
import { FeedbackOutlined, Add } from "@mui/icons-material";

import { useFeedbackDetailContext } from "contexts/FeedbackDetailContext";

import FormDrawer from "components/Shared/FormDrawer";
import ResponseForm from "components/Response/ResponseForm";
import ResponseList from "components/Response/ResponseList";

import {
  useAddFeedbackResponseMutation,
  useUpdateFeedbackResponseMutation,
} from "store/services/feedbackApi";

const FeedbackResponsesTab = () => {
  const [showResponseForm, setShowResponseForm] = React.useState(false);
  const [selectedResponse, setSelectedResponse] = React.useState(null);

  const { id: feedbackId } = useParams();

  const { feedbackDetail } = useFeedbackDetailContext();

  const sortedResponses = [...feedbackDetail?.responses]?.sort((a, b) =>
    a?.createdAt > b?.createdAt ? -1 : 1
  );

  // RTK Query
  const [addResponse, { isLoading: addingResponse }] =
    useAddFeedbackResponseMutation();

  const [updateResponse, { isLoading: updatingResponse }] =
    useUpdateFeedbackResponseMutation();

  // methods
  const handleCancelResponse = () => {
    setShowResponseForm(false);
    setSelectedResponse(null);
  };

  const handleAddResponse = async (data) => {
    const payload = {
      ...data,
      feedbackId,
    };

    try {
      await addResponse(payload).unwrap();
      toast.success("Response successfully added!");
      handleCancelResponse();
    } catch (err) {
      toast.error(err?.data?.message ?? "Couldn't add response!");
    }
  };

  const handleEditResponse = (response) => {
    setShowResponseForm(true);
    setSelectedResponse(response);
  };

  const handleUpdateResponse = async (data) => {
    try {
      await updateResponse({
        ...data,
        feedbackId,
      }).unwrap();
      toast.success("Response successfully updated!");
      handleCancelResponse();
    } catch (err) {
      toast.error(err?.data?.message ?? "Couldn't update response!");
    }
  };

  return (
    <>
      <Box
        display="flex"
        alignItems="end"
        justifyContent="space-between"
        padding={3}
      >
        <Box display="inline-flex" alignItems="center" gap={1.5}>
          <Badge badgeContent={sortedResponses?.length} color="primary">
            <FeedbackOutlined style={{ color: "gray" }} />
          </Badge>

          <Typography style={{ fontWeight: 500 }}>Responses</Typography>
        </Box>

        {!showResponseForm && (
          <Button
            startIcon={<Add />}
            variant="outlined"
            onClick={() => setShowResponseForm(true)}
          >
            Response
          </Button>
        )}
      </Box>

      <Divider />

      <ResponseList
        responses={feedbackDetail?.responses}
        onEditAction={handleEditResponse}
      />

      <FormDrawer
        title="Feedback Response"
        open={showResponseForm}
        onClose={handleCancelResponse}
      >
        <ResponseForm
          isBusy={addingResponse || updatingResponse}
          response={selectedResponse}
          onSubmit={(data) =>
            selectedResponse
              ? handleUpdateResponse(data)
              : handleAddResponse(data)
          }
          onCancel={handleCancelResponse}
        />
      </FormDrawer>
    </>
  );
};

export default FeedbackResponsesTab;
